import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MediaCard from '@/components/base/MediaCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { searchMedia, getHotRanking, typeLabels } from '@/utils/media';
import type { MediaType } from '@/mocks/movies';

type FilterType = 'all' | MediaType;

export default function SearchPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [activeType, setActiveType] = useState<FilterType>('all');

  useEffect(() => {
    setInput(q);
  }, [q]);

  usePageMeta(
    q ? `搜索“${q}” - 久久精品影视资源` : `${t('searchTitle')} - 久久精品影视资源`,
    `在久久久精品中搜索“${q}”相关的电影、电视剧与综艺，快速找到你想要的影视内容。`,
    `久久久精品,影视搜索,${q || '影视资源'}`,
  );

  const results = useMemo(() => searchMedia(q, activeType), [q, activeType]);
  const recommendations = getHotRanking(5);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const typeTabs: { key: FilterType; label: string }[] = [
    { key: 'all', label: t('all') },
    { key: 'movie', label: typeLabels.movie },
    { key: 'tv', label: typeLabels.tv },
    { key: 'variety', label: typeLabels.variety },
  ];

  return (
    <div className="jjj-fade-up">
      <div className="bg-primary-950">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <nav className="mb-4 flex items-center gap-2 text-xs text-background-200/70">
            <Link to="/" className="transition hover:text-accent-300 cursor-pointer">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{t('navSearch')}</span>
          </nav>
          <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">{t('searchTitle')}</h1>
          <form onSubmit={submit} className="mt-5 flex max-w-2xl items-center gap-2 rounded-md border border-background-50/20 bg-background-50/10 px-4 py-3 backdrop-blur">
            <i className="ri-search-line text-lg text-background-200"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-background-200/60 focus:outline-none"
            />
            <button type="submit" className="rounded-md bg-accent-500 px-4 py-1.5 text-sm font-semibold text-accent-950 transition hover:bg-accent-400 cursor-pointer whitespace-nowrap">
              {t('navSearch')}
            </button>
          </form>
          {q ? (
            <p className="mt-4 text-sm text-background-100/80">
              {t('searchResultFor')} <span className="font-semibold text-accent-400">“{q}”</span>
              <span className="mx-2 text-background-200/40">|</span>
              {results.length} {t('results')}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground-600">{t('filterType')}</span>
          <div className="inline-flex flex-wrap gap-2">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveType(tab.key)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                  activeType === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {!q ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <i className="ri-search-line text-5xl text-foreground-300"></i>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">支持按影片名、类型、地区、演职员等内容进行模糊搜索</p>
          </div>
        ) : results.length === 0 ? (
          <div>
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{t('noSearchTitle')}</h3>
              <p className="mt-2 text-sm text-foreground-500">{t('noSearchDesc')}</p>
            </div>
            <div className="mt-10">
              <h3 className="mb-4 font-heading text-lg font-bold text-foreground-950">{t('recommendForYou')}</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                {recommendations.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {results.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}